import BrandModal from '@/common/atoms/BrandModal'
import OnboardingProgress from '@/onboarding/components/OnboardingProgress'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { Button, Flex, Spacer, useToast } from '@chakra-ui/react'
import { useUpdateOrgMutation } from '@gql'
import { nanoid } from 'nanoid'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeftIcon, ChevronRightIcon } from 'src/icons'
import useSeedOrg, { createSeedOrgProgress } from '../hooks/useSeedOrg'
import { OrgType } from '../orgTypes'
import OrgSetupStepModel from './OrgSetupStepModel'
import OrgSetupStepRoles from './OrgSetupStepRoles'
import { RoleDraft } from './orgSetupTypes'

interface Props {
  onClose(): void
}

enum SetupStep {
  Model = 'Model',
  Roles = 'Roles',
}

// Org-setup steps shown right after an org is created: pick the organizational
// model, then — unless the org is empty or the help is switched off — define
// the main roles (with their leader and participants). "Create" seeds the
// chart and the meeting templates.
export default function OrgSetupModal({ onClose }: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const { orgId, orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const [updateOrg] = useUpdateOrgMutation()
  const seedOrg = useSeedOrg()

  const [step, setStep] = useState<SetupStep>(SetupStep.Model)
  const [orgType, setOrgType] = useState<OrgType>(OrgType.Classic)
  const [roles, setRoles] = useState<RoleDraft[]>([])
  const [rolesHelp, setRolesHelp] = useState(true)
  const [loading, setLoading] = useState(false)
  // Survives a failed finish, so a second click resumes instead of duplicating
  const seedProgress = useRef(createSeedOrgProgress())

  const rootCircle = useMemo(
    () => orgData?.circles.find((c) => !c.parentId),
    [orgData]
  )

  const withRoles = orgType !== OrgType.Empty && rolesHelp
  const stepOrder = withRoles
    ? [SetupStep.Model, SetupStep.Roles]
    : [SetupStep.Model]
  const isLastStep = stepOrder.indexOf(step) === stepOrder.length - 1

  // Each main role needs a name and a responsible before we can create
  const canSeed =
    !withRoles ||
    (roles.length > 0 && roles.every((r) => r.name && r.responsibleId))

  const addRole = (name: string) =>
    setRoles((rs) => [...rs, { id: nanoid(8), name, participantIds: [] }])

  const updateRole = (id: string, patch: Partial<RoleDraft>) =>
    setRoles((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)))

  const removeRole = (id: string) =>
    setRoles((rs) => rs.filter((r) => r.id !== id))

  // Finish: persist the model, seed the chart + meeting templates, then close.
  // Members already exist (created while assigning roles), so seeding happens
  // only here. Roles typed before switching the help off are not created.
  const finish = async () => {
    // The creator's member id is needed to seat them (see useSeedOrg)
    if (!orgId || !rootCircle || !currentMember) return
    setLoading(true)
    try {
      await updateOrg({
        variables: { id: orgId, values: { onboardingOrgType: orgType } },
      })
      await seedOrg({
        orgType,
        rootCircleId: rootCircle.id,
        roles: (withRoles ? roles : []).map((r) => ({
          id: r.id,
          name: r.name,
          responsibleId: r.responsibleId,
          participantIds: r.participantIds,
        })),
        creatorMemberId: currentMember.id,
        progress: seedProgress.current,
      })
      toast({
        title: t('OrgSetupModal.ready'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
      onClose()
    } catch (error: any) {
      toast({
        title: error?.message || t('common.error'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
      setLoading(false)
    }
  }

  return (
    <BrandModal
      size="2xl"
      bodyProps={{ mx: 10 }}
      backButton={false}
      isOpen
      autoFocus={false}
      closeOnEsc={false}
      onClose={onClose}
    >
      <OnboardingProgress
        total={stepOrder.length}
        current={stepOrder.indexOf(step)}
      />

      {step === SetupStep.Model && (
        <OrgSetupStepModel
          orgType={orgType}
          onChange={setOrgType}
          rolesHelp={rolesHelp}
          onRolesHelpChange={setRolesHelp}
        />
      )}
      {step === SetupStep.Roles && (
        <OrgSetupStepRoles
          orgType={orgType}
          roles={roles}
          onAdd={addRole}
          onChange={updateRole}
          onRemove={removeRole}
        />
      )}

      <Flex mt={8} align="center">
        {stepOrder.indexOf(step) > 0 && (
          <Button
            variant="ghost"
            leftIcon={<ChevronLeftIcon size="1em" />}
            onClick={() => setStep(stepOrder[stepOrder.indexOf(step) - 1])}
            isDisabled={loading}
          >
            {t('common.back')}
          </Button>
        )}
        <Spacer />

        {isLastStep ? (
          <Button
            colorScheme="blue"
            onClick={finish}
            isLoading={loading}
            isDisabled={!canSeed}
          >
            {t('common.create')}
          </Button>
        ) : (
          <Button
            colorScheme="blue"
            rightIcon={<ChevronRightIcon size="1em" />}
            onClick={() => setStep(stepOrder[stepOrder.indexOf(step) + 1])}
          >
            {t('common.next')}
          </Button>
        )}
      </Flex>
    </BrandModal>
  )
}
